import { ChangeDetectionStrategy, Component, computed, effect, inject, model, output, signal } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import moment from 'moment';
import { SharedModule } from '@shared/shared.module';
import { CONFIRMER } from '@shared/components/dialogs/confirm/types';
import { NOTIFIER } from '@shared/notifier/types';
import { ClipboardService } from '@shared/services/clipboard.service';
import { PlatformChartListItemDto } from '@data/_generated-api/platform-api/models/platform-chart-list-item-dto';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import { HdChartShareService } from './hd-chart-share.service';
import { HdChartShareChange, buildHdChartShareUrl } from './hd-chart-share.models';

const DATE_TIME_FORMAT = 'DD-MM-YYYY HH:mm';

/**
 * Overzicht "Gedeelde charts" in de chartmanager (wens Steven 2026-10-20): alle
 * eigen charts met een werkende deellink, met per rij de link kopiëren of het
 * delen stoppen, en onderaan "Alles stoppen". Klik op een naam laadt die chart.
 */
@Component({
  selector: 'hd-chart-shared-list-dialog',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './hd-chart-shared-list-dialog.component.html',
  styleUrl: './hd-chart-shared-list-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdChartSharedListDialogComponent {
  private readonly shareService = inject(HdChartShareService);
  private readonly clipboard = inject(ClipboardService);
  private readonly confirmer = inject(CONFIRMER);
  private readonly notifier = inject(NOTIFIER);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  readonly open = model(false);
  /** Klik op een rij: chart openen (de manager doet de load). */
  readonly chartOpenRequested = output<string>();
  /** Per gestopte share, zodat de manager zijn lijst lokaal kan bijwerken. */
  readonly shareChanged = output<HdChartShareChange>();

  protected readonly items = signal<PlatformChartListItemDto[]>([]);
  protected readonly loading = signal(false);
  protected readonly error = signal('');
  /** Loopt "Alles stoppen": knoppen dicht tot de call klaar is. */
  protected readonly stoppingAll = signal(false);

  constructor() {
    effect(() => {
      if (this.open()) void this.load();
    });
  }

  protected readonly text = computed(() => {
    this.contentLang.translationsLoadedTick();
    const language = this.contentLang.effectiveLang();
    const translate = (key: string) => this.transloco.translate<string>(key, undefined, language);
    return {
      title: translate('humanDesign.chartManager.sharedCharts.title'),
      empty: translate('humanDesign.chartManager.sharedCharts.empty'),
      copyLink: translate('humanDesign.chartManager.sharedCharts.copyLink'),
      copied: translate('humanDesign.chartManager.sharedCharts.copied'),
      stop: translate('humanDesign.chartManager.sharedCharts.stop'),
      stopAll: translate('humanDesign.chartManager.sharedCharts.stopAll'),
      stopped: translate('humanDesign.chartManager.sharedCharts.stopped'),
      loading: translate('humanDesign.chartManager.detail.loading'),
    };
  });

  protected onOpenChange(open: boolean): void {
    this.open.set(open);
  }

  protected formatSharedOn(timestamp: string | null | undefined): string {
    if (!timestamp) return '—';
    const parsed = moment(timestamp);
    return parsed.isValid() ? parsed.format(DATE_TIME_FORMAT) : '—';
  }

  protected openChart(item: PlatformChartListItemDto): void {
    if (!item.id) return;
    this.chartOpenRequested.emit(item.id);
    this.open.set(false);
  }

  protected copyLink(item: PlatformChartListItemDto): void {
    if (!item.shareToken) return;
    this.clipboard.copyToClipBoard(buildHdChartShareUrl(item.shareToken));
    this.notifier.success(this.text().copied);
  }

  /** Delen van één chart stoppen; de link werkt meteen niet meer. */
  protected async stop(item: PlatformChartListItemDto): Promise<void> {
    if (!item.id) return;
    const message = this.transloco.translate<string>(
      'humanDesign.chartManager.sharedCharts.stopConfirm',
      { name: item.name },
      this.contentLang.effectiveLang(),
    );
    if (!(await this.confirmer.confirm(message, { color: 'danger' }))) return;

    this.error.set('');
    try {
      const change = await this.shareService.unshare(item.id);
      this.items.update(current => current.filter(candidate => candidate.id !== item.id));
      this.shareChanged.emit(change);
      this.notifier.success(this.text().stopped);
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : `${e}`);
    }
  }

  /** Alle deellinks in één keer intrekken (server-side ook die van charts in de prullenbak). */
  protected async stopAll(): Promise<void> {
    const targets = this.items().filter(item => !!item.id);
    if (targets.length === 0 || this.stoppingAll()) return;
    const message = this.transloco.translate<string>(
      'humanDesign.chartManager.sharedCharts.stopAllConfirm',
      { count: targets.length },
      this.contentLang.effectiveLang(),
    );
    if (!(await this.confirmer.confirm(message, { color: 'danger' }))) return;

    this.error.set('');
    this.stoppingAll.set(true);
    try {
      const count = await this.shareService.unshareAll();
      this.items.set([]);
      for (const item of targets) this.shareChanged.emit({ id: item.id!, shareToken: null });
      this.notifier.success(
        this.transloco.translate<string>(
          'humanDesign.chartManager.sharedCharts.allStopped',
          { count },
          this.contentLang.effectiveLang(),
        ),
      );
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : `${e}`);
    } finally {
      this.stoppingAll.set(false);
    }
  }

  private async load(): Promise<void> {
    this.loading.set(true);
    this.error.set('');
    try {
      this.items.set(await this.shareService.listSharedByMe());
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : `${e}`);
    } finally {
      this.loading.set(false);
    }
  }
}
